import { MouseEvent, MouseEventHandler, useCallback, useRef } from 'react';

export type CallbackFunction<Target = Element> = MouseEventHandler<Target>;

export type DoubleTapCallback<Target = Element> = CallbackFunction<Target> | null;

export type DoubleTapResult<Target, Callback> = {
  onClick: CallbackFunction<Target>;
};

export function useDoubleTap<Target = Element, Callback extends DoubleTapCallback<Target> = DoubleTapCallback<Target>>(
  callback: Callback,
  threshold: number = 400,
): DoubleTapResult<Target, Callback> {
  const timer = useRef<NodeJS.Timeout | null>(null);

  const handler = useCallback<CallbackFunction<Target>>(
    (event: MouseEvent<Target>) => {
      if (!timer.current) {
        timer.current = setTimeout(() => {
          timer.current = null;
        }, threshold);
      } else {
        clearTimeout(timer.current);
        timer.current = null;
        callback && callback(event);
      }
    },
    [callback, threshold],
  );

  return {
    onClick: handler,
  } as DoubleTapResult<Target, Callback>;
}
